import type { ScanCommandOutput } from "@aws-sdk/lib-dynamodb"
import type { Device, VehicleStatusResponse } from "@strtmn/sds/types"
import { getErrorMessage } from "@strtmn/strtmn"
import { Cache } from "aws-amplify/utils"
import { isDev, isProd, isStaging } from "../config"
import { isUserSignedIn } from "../Utils.AmplifyAuth"
import { getCacheKey, getFromApi } from "./utils"

// 10 minutes
const EXPIRES_IN_MS = 10 * 60 * 1000

// Lists vehicles from DDB
export const listVehicles = async (): Promise<Device[] | false> => {
  console.log(`> listVehicles()`)
  const apiName = "vehiclesApi"
  const path = `/vehicles`
  const cacheKey = getCacheKey(apiName, path)

  if (!(await isUserSignedIn())) {
    return false
  }

  try {
    // !isProd && console.debug('try to get item from cache')
    // console.warn("remove item from cache") // DBG
    // await Cache.removeItem(cacheKey) // DBG
    let data: Device[] = await Cache.getItem(cacheKey)
    if (data) {
      console.log("listVehicles: Found in cache:", data, `${cacheKey}`)
    } else {
      console.log(
        "listVehicles:",
        cacheKey,
        "not found in cache. Loading from API..."
      )
      const response = (await getFromApi(apiName, path))
        .json as ScanCommandOutput
      !isProd && console.log("listVehicles: response from api =", response)
      data = response.Items!.map((elem) => {
        return {
          ...elem,
          vin: elem["ext_sn"],
          modem_sn: elem["seriennummer"],
        }
      })
      // optional: do sorting
      await Cache.setItem(cacheKey, data, {
        expires: new Date().getTime() + EXPIRES_IN_MS,
      })
      !isProd && console.debug("listVehicles: Fetched from api:", data)
    }

    // DNV data actually comes from Supa, so should be in the MachinesApi
    data.forEach((device) => {
      if (isProd) {
        // Pretend an active DNV, this is the least disturbing.
        const hasActiveDnv = true
        device["hasActiveDnv"] = hasActiveDnv
      }
      if (isStaging) {
        // Pretend an active DNV, until the ui has settled a bit.
        const hasActiveDnv = true
        device["hasActiveDnv"] = hasActiveDnv
      }
      if (isDev) {
        const hasActiveDnv = "abc126" === device.vin
        device["hasActiveDnv"] = hasActiveDnv
      }
    })

    // !isProd && console.debug("✅ listVehicles: data")
    return data
  } catch (err) {
    console.error(
      "listVehicles: Error fetching Vehicles data from cache (unlikely) or api (likely)."
    )
    console.log("listVehicles: Error:", err)
    if (isDev) {
      const errMsg = getErrorMessage(err)
      if (errMsg.includes("NetworkError")) {
        const apiMsg = `API call was to ${apiName} ${path}`
        console.log(apiMsg)
        const errAsError = err as Error
        if ("body" in errAsError) {
          errAsError.body += "\n" + apiMsg
        }
      }
    }
    throw err
  }
}

export const getVehicleStatus = async (
  vin: string
): Promise<({ vin: string } & VehicleStatusResponse) | false> => {
  console.debug(`> getVehicleStatus(${vin})`)
  const apiName = "vehiclesApi"
  const path = `/vehicle/${vin}/status`

  if (!(await isUserSignedIn())) {
    return false
  }

  try {
    console.debug("getVehicleStatus is uncached. Loading from API...")
    const response = (await getFromApi(apiName, path))
      .json as VehicleStatusResponse

    const data = {
      vin: vin,
      ...response,
    }
    // .Items!.map(elem => {
    //   elem.vin = elem.ext_sn
    //   elem.modem_sn = elem.seriennummer
    //   return elem
    // })[0]
    // console.log(response)
    !isProd && console.debug("getVehicleStatus: Fetched from api:", data)
    return data
  } catch (err) {
    console.error("getVehicleStatus: Error fetching Vehicle status from api.")
    console.log("getVehicleStatus: Error:", err)
    if (isDev) {
      const errMsg = getErrorMessage(err)
      if (errMsg.includes("NetworkError")) {
        const apiMsg = `API call was to ${apiName} ${path}`
        console.log(apiMsg)
        const errAsError = err as Error
        if ("body" in errAsError) {
          errAsError.body += "\n" + apiMsg
        }
      }
    }
    throw err
  }
}

// export const getById = async (
//   VehicleId: string
// ): Promise<Device | false> => {
//   console.log(`> getById()`)
//   const apiName = "frontendApi"
//   const path = `/machines/${VehicleId}`
//   const cacheKey = getCacheKey(apiName, path)

//   if (!(await isUserSignedIn())) {
//     return false
//   }

//   try {
//     // !isProd && console.debug("try to get item from cache")
//     console.warn("remove item from cache") // DBG
//     await Cache.removeItem(cacheKey) // DBG
//     let data: Device | null = await Cache.getItem(cacheKey)
//     if (data) {
//       console.log("getById: Found in cache:", data, `${cacheKey}`)
//     } else {
//       console.log(
//         "getById:",
//         cacheKey,
//         "not found in cache. Loading from API..."
//       )
//       data = (await getFromApi(apiName, path)).json as Device
//       await Cache.setItem(cacheKey, data, {
//         expires: new Date().getTime() + EXPIRES_IN_MS,
//       })
//       !isProd && console.debug("getById: Fetched from api:", data)
//     }

//     // !isProd && console.debug("✅ getById: data")
//     return data
//   } catch (err) {
//     console.error(
//       "getById: Error fetching Vehicle data from cache (unlikely) or api (likely)."
//     )
//     console.log("vehicles.getById: Error:", err)
//     throw err
//   }
// }

// export const sortVehicles = (vehicles: Device[]) => {
//   console.debug(`> sortOrgs (${vehicles.length} orgs)`)
//   const pushM1ToBack = 1
//   const pushM2ToBack = -1
//   // machines.forEach((o: MachineSupaDto) => {
//   //   o.adresseKurz =
//   //     o.adresse && o.adresse.ort && o.adresse.strasse
//   //       ? `${o.adresse.ort}, ${o.adresse.strasse}`
//   //       : undefined
//   // })
//   // console.log('res: ', items)

//   vehicles.sort((m1: Device, m2: Device) => {
//     // Sort by name
//     if (m1.name < m2.name) {
//       return pushM2ToBack
//     }
//     if (m1.name > m2.name) {
//       return pushM1ToBack
//     }
//     // Sort by serial (VIN)
//     if (m1.serial_number < m2.serial_number) {
//       return pushM2ToBack
//     }
//     if (m1.serial_number > m2.serial_number) {
//       return pushM1ToBack
//     }

//     return 0
//   })
//   return vehicles
// }

// export const filterVehicles = (
//   vehicles: Device[],
//   filter: string | undefined
// ) => {
//   if (!filter || !vehicles) {
//     return vehicles
//   }
//   return vehicles.filter((vehicle) => {
//     const haystack = [
//       vehicle.serial_number,
//       vehicle.modem_serial,
//       vehicle.name,
//       vehicle.Vehicle_type,
//       vehicle.Vehicle_attributes,
//     ]
//       .join("#")
//       .toLowerCase()
//     return haystack.includes(filter)
//   })
// }

// const clearCacheVehicleData = async (vehicleId: string): Promise<void> => {
//   console.log(`> clearCacheOrgData(${vehicleId})`)
//   const apiName = "frontendApi"
//   const path = `/vehicles/${vehicleId}`
//   const cacheKey = getCacheKey(apiName, path)
//   !isProd && console.debug("Remove item from cache:", cacheKey)
//   await Cache.removeItem(cacheKey)
// }
